import React, { useState, useEffect } from "react";
import api from "../../lib/api";
import { Plus, Edit2, Trash2, X, Save } from "lucide-react";
import { useConfirm } from "../../contexts/ConfirmContext";

const TYPES = ["Router", "Switch", "Server", "Workstation", "Drucker", "NAS", "Firewall", "AP", "Sonstiges"];

function EntryRow({ entry, customerId, onDelete, onUpdate }) {
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState({ ...entry });

  const save = async (e) => {
    e.preventDefault();
    const r = await api.put(`/customers/${customerId}/network/${entry._id}`, form);
    onUpdate(r.data);
    setShowEdit(false);
  };

  return (
    <>
      <tr data-testid={`network-row-${entry._id}`}>
        <td className="font-medium">{entry.name}</td>
        <td className="text-xs text-muted-foreground">{entry.type || "—"}</td>
        <td className="font-mono text-xs">{entry.ip_address || "—"}</td>
        <td className="font-mono text-xs">{entry.subnet || "—"}</td>
        <td className="font-mono text-xs">{entry.gateway || "—"}</td>
        <td className="font-mono text-xs">{entry.dns_primary || "—"}</td>
        <td className="font-mono text-xs">{entry.dns_secondary || "—"}</td>
        <td>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowEdit(!showEdit)} className="text-muted-foreground hover:text-primary transition-colors"><Edit2 size={13} /></button>
            <button onClick={() => onDelete(entry._id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 size={13} /></button>
          </div>
        </td>
      </tr>
      {showEdit && (
        <tr>
          <td colSpan={8} className="bg-muted/30 p-4">
            <form onSubmit={save} className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { name: "name", label: "Name *", required: true },
                { name: "ip_address", label: "IP-Adresse" },
                { name: "subnet", label: "Subnetz" },
                { name: "gateway", label: "Gateway" },
                { name: "dns_primary", label: "DNS Primär" },
                { name: "dns_secondary", label: "DNS Sekundär" },
                { name: "mac_address", label: "MAC-Adresse" },
                { name: "vlan", label: "VLAN" },
                { name: "notes", label: "Notizen" },
              ].map(f => (
                <div key={f.name}>
                  <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">{f.label}</label>
                  <input value={form[f.name] || ""} onChange={e => setForm({ ...form, [f.name]: e.target.value })} required={f.required}
                    className="w-full px-2 py-1.5 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none" />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Typ</label>
                <select value={form.type || ""} onChange={e => setForm({ ...form, type: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                  <option value="">—</option>
                  {TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="col-span-full flex gap-2">
                <button type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 flex items-center gap-1"><Save size={12} /> Speichern</button>
                <button type="button" onClick={() => setShowEdit(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold flex items-center gap-1"><X size={12} /> Abbrechen</button>
              </div>
            </form>
          </td>
        </tr>
      )}
    </>
  );
}

export default function NetworkTab({ customerId }) {
  const confirm = useConfirm();
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", type: "", ip_address: "", subnet: "", gateway: "", dns_primary: "", dns_secondary: "", mac_address: "", vlan: "", notes: "" });

  useEffect(() => {
    api.get(`/customers/${customerId}/network`).then(r => setEntries(r.data));
  }, [customerId]);

  const create = async (e) => {
    e.preventDefault();
    const r = await api.post(`/customers/${customerId}/network`, form);
    setEntries([...entries, r.data]);
    setForm({ name: "", type: "", ip_address: "", subnet: "", gateway: "", dns_primary: "", dns_secondary: "", mac_address: "", vlan: "", notes: "" });
    setShowForm(false);
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Netzwerk-Eintrag löschen", message: "Dieser Eintrag wird dauerhaft entfernt.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/customers/${customerId}/network/${id}`);
    setEntries(entries.filter(e => e._id !== id));
  };

  return (
    <div className="max-w-6xl animate-in">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Netzwerk & IP</h2>
        <button data-testid="add-network-button" onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">
          <Plus size={13} /> Neuer Eintrag
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-card border border-border p-4 mb-4 animate-in">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: "name", label: "Name *", required: true },
              { name: "ip_address", label: "IP-Adresse" },
              { name: "subnet", label: "Subnetz" },
              { name: "gateway", label: "Gateway" },
              { name: "dns_primary", label: "DNS Primär" },
              { name: "dns_secondary", label: "DNS Sekundär" },
              { name: "mac_address", label: "MAC-Adresse" },
              { name: "vlan", label: "VLAN" },
              { name: "notes", label: "Notizen" },
            ].map(f => (
              <div key={f.name}>
                <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">{f.label}</label>
                <input data-testid={`network-form-${f.name}`} value={form[f.name]} onChange={e => setForm({ ...form, [f.name]: e.target.value })}
                  required={f.required} className="w-full px-3 py-2 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none" />
              </div>
            ))}
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-muted-foreground block mb-1">Typ</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none">
                <option value="">—</option>
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button data-testid="save-network-button" type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">Speichern</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold">Abbrechen</button>
          </div>
        </form>
      )}

      <div className="bg-card border border-border overflow-x-auto" data-testid="network-table">
        {entries.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Keine Netzwerkeinträge</div>
        ) : (
          <table className="w-full data-table">
            <thead><tr><th>Name</th><th>Typ</th><th>IP</th><th>Subnetz</th><th>Gateway</th><th>DNS Primär</th><th>DNS Sekundär</th><th></th></tr></thead>
            <tbody>
              {entries.map(e => <EntryRow key={e._id} entry={e} customerId={customerId} onDelete={remove} onUpdate={u => setEntries(entries.map(x => x._id === u._id ? u : x))} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
